/// <amd-module name="Feed/Legacy/Protocols/Components/CopyableTextField" />
import * as React from "react";

import { DefaultButton, IButton } from "OfficeFabric/Button";
import { DirectionalHint } from "OfficeFabric/common/DirectionalHint";
import { BaseComponent, IBaseProps } from "OfficeFabric/Utilities";

import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { IVssPageContext } from "VSS/Platform/Context";
import { css } from "VSS/Platform/Layout";

import { TooltipHost } from "VSSUI/Tooltip";

import { CiConstants } from "Feed/Common/Constants/Constants";
import * as PackageResources from "Feed/Common/Resources";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { copyToClipboard } from "Feed/Legacy/Utils/CopyToClipboard";

export interface ICopyableTextFieldTelemetry {
    commandName: string;
    feedName: string;
    packageName?: string;
    packageVersion?: string;
    protocol: string;
    clientTool?: string;
}

export interface ICopyableTextFieldProps extends IBaseProps {
    pageContext: IVssPageContext;
    text: string;
    // optional: when text shown to user needs to be different from what is copied to clipboard
    displayText?: string;
    telemetryProperties: ICopyableTextFieldTelemetry;
    buttonAriaLabel: string;
    textFieldAriaLabel: string;
    /*Id of the element for aria-describedby.
    Only provide this if you have assigned the same id to the element that describes the copiable text*/
    ariaDescribedByIdForLabelText?: string;
    // Optional Css class to put in child nodes
    cssClassToOverWrite?: string;
}

export interface ICopyableTextFieldState {
    wasCopied: boolean;
}

export class CopyableTextField extends BaseComponent<ICopyableTextFieldProps, ICopyableTextFieldState> {
    private _copyButton: IButton = null;

    constructor(props: ICopyableTextFieldProps) {
        super(props);
        this.state = {
            wasCopied: false
        };
    }

    public focus(): void {
        this._async.setImmediate(() => {
            if (this._copyButton) {
                this._copyButton.focus();
            }
        });
    }

    public render(): JSX.Element {
        const tooltipText = this.state.wasCopied
            ? PackageResources.CopiedToClipboard
            : PackageResources.CopyToClipboard;

        return (
            <div className={"copyable-text-field bowtie"}>
                <div className={"copyable-text-field-text-tooltip-container"}>{this._getLabel()}</div>
                <TooltipHost content={tooltipText} directionalHint={DirectionalHint.bottomCenter}>
                    <DefaultButton
                        onClick={this._onCopy}
                        className={"copy-button"}
                        onBlur={() => this.setState({ wasCopied: false })}
                        onMouseLeave={() => this.setState({ wasCopied: false })}
                        ariaLabel={this.props.buttonAriaLabel}
                        componentRef={self => (this._copyButton = self)}
                    >
                        <span className={"bowtie-icon bowtie-edit-copy"} />
                    </DefaultButton>
                </TooltipHost>
            </div>
        );
    }

    private _getLabel(): JSX.Element {
        const lines = this._replaceNewLines();
        const divClassName = css("copyable-text-field-text", this.props.cssClassToOverWrite || "");
        return (
            <label
                className={divClassName}
                aria-describedby={
                    this.props.ariaDescribedByIdForLabelText ? this.props.ariaDescribedByIdForLabelText : null
                }
                aria-label={this.props.textFieldAriaLabel ? this.props.textFieldAriaLabel : null}
            >
                {lines.map(line => line)}
            </label>
        );
    }

    private _onCopy = (): void => {
        copyToClipboard(this.props.text);

        Utils_Accessibility.announce(PackageResources.CopiedToClipboard);
        this.setState({ wasCopied: true });

        this.focus();
        Actions.CopyToClipboard.invoke(null);
        CustomerIntelligenceHelper.publishEvent(
            this.props.pageContext,
            CiConstants.CopyCommand,
            this.props.telemetryProperties
        );
    };

    private _replaceNewLines(): React.ReactNode[] {
        const content: React.ReactNode[] = [];
        let displayText: string = this.props.displayText || this.props.text;
        if (displayText == null) {
            displayText = "";
        }
        const lines = displayText.split(/\r\n|\r|\n/g);
        content.push(lines[0]);
        for (let i = 1; i < lines.length; i++) {
            content.push(<br key={i} />);
            content.push(lines[i]);
        }

        return content;
    }
}
